<template>
  <div class="department-card" :class="{ 'department-card--idea-mdp-450': ideaMDP450 }">
    <div v-if="!hideHeader" class="department-card__header-wrapper">
      <ExBreadcrumbs
        class="department-card__breadcrumbs"
        :breadcrumbsList="departmentBreadcrumbs"
        @onClickBreadcrumb="handleBreadcrumbClick"
      />
      <DepartmentPageHeader
        :name="name"
        :code="code"
        :status="status"
        :readiness="displayedReadiness"
        :chatter-unread-count="chatterUnreadCount"
        :menu-items="menuItems"
        @chatter-click="$emit('open-department-chatter')"
        @menu-click="$emit('open-department-menu', $event)"
      >
        <template v-if="$slots['department-icon']" #icon>
          <slot name="department-icon" />
        </template>
      </DepartmentPageHeader>

      <div
        v-if="showReadinessScopeToggle"
        class="department-card__readiness-scope"
        role="radiogroup"
        aria-label="Readiness scope"
      >
        <button
          type="button"
          class="department-card__readiness-scope-option"
          :class="{
            'department-card__readiness-scope-option--active':
              activeReadinessScope === DEPARTMENT_READINESS_SCOPE.SUBTREE,
          }"
          role="radio"
          :aria-checked="
            activeReadinessScope === DEPARTMENT_READINESS_SCOPE.SUBTREE ? 'true' : 'false'
          "
          @click="setReadinessScope(DEPARTMENT_READINESS_SCOPE.SUBTREE)"
        >
          Including sub-departments
        </button>
        <span class="department-card__readiness-scope-divider" aria-hidden="true" />
        <button
          type="button"
          class="department-card__readiness-scope-option"
          :class="{
            'department-card__readiness-scope-option--active':
              activeReadinessScope === DEPARTMENT_READINESS_SCOPE.SELF,
          }"
          role="radio"
          :aria-checked="
            activeReadinessScope === DEPARTMENT_READINESS_SCOPE.SELF ? 'true' : 'false'
          "
          @click="setReadinessScope(DEPARTMENT_READINESS_SCOPE.SELF)"
        >
          This department only
        </button>
      </div>

      <div class="department-card__tabs">
        <EmployeeHorizontalMenu
          :menu-items="tabMenuItems"
          :initial-tab="activeTabLabel"
          @tab-selected="onTabSelected"
        />
      </div>
    </div>

    <!-- Positions tab -->
    <div v-if="activeTab === TABS.POSITIONS" class="department-card__body" role="tabpanel">
      <div
        v-if="positions.length > 0 || filterChips.length > 0 || hasSearch"
        class="department-card__filters"
        :class="{ 'department-card__filters--wide-search': wideUnassignedSearch }"
      >
        <SearchBar
          :label="filterChips.length > 0 ? 'More filters' : 'Filter'"
          :length="filterChips.length"
          :chip-text="filterChipLabels"
          :ghost-text="searchPlaceholder"
          :value-search="searchValue"
          :add="false"
          type="button"
          :counter-section="false"
          :flexible-layout="true"
          :filter-btn="!hideFilters"
          @onClick="onFilterClick"
          @removeFilter="onRemoveFilter"
          @onSearch="onSearch"
        />
      </div>

      <NoRecordFound
        v-if="positions.length === 0"
        :should-show-add-action="false"
        :title="''"
        :message="'positions'"
        :showSpecificMessage="noRecordFoundSpecificMessage"
        :description="noRecordFoundDescription"
        :noBorder="true"
        :showClearAction="filterChips.length > 0 || hasSearch"
        :clearLabel="clearLabel"
        :showActiveCounter="false"
        @onClear="onClearFilters"
      />

      <div
        v-if="positions.length > 0"
        ref="positionsGridWrapperRef"
        class="department-card-positions-grid-wrapper roles-table-wrapper"
        :class="{ 'roles-table-wrapper--with-drag-col': showPositionDragColumn }"
      >
        <DxDataGrid
          :data-source="gridPositions"
          :column-auto-width="false"
          :word-wrap-enabled="true"
          :show-row-lines="true"
          :show-column-lines="false"
          :show-borders="false"
          :element-attr="positionsGridElementAttr"
          height="auto"
          :scrolling="{
            mode: 'standard',
            useNative: true,
            scrollByContent: true,
            scrollByThumb: true,
            showScrollbar: 'never',
          }"
          @content-ready="onPositionsGridContentReady"
        >
          <DxPaging :enabled="false" />
          <DxPager :visible="false" />
          <DxRowDragging
            v-if="!allowDragPositionToTree"
            :allow-reordering="true"
            :show-drag-icons="false"
            handle=".department-card__drag-handle"
            @reorder="onReorder"
          />

          <DxColumn
            v-if="showPositionDragColumn"
            css-class="position-attached-role-unified-col"
            :allow-sorting="false"
            cell-template="positionUnifiedDragHandleCell"
            width="52px"
          />
          <template #positionUnifiedDragHandleCell="{ data }">
            <div
              v-if="!isSubDepartmentRow(data.data)"
              class="drag-handle-cell-wrapper position-role-reorder-handle position-role-tree-drag-handle"
              :draggable="
                data.data?.id != null && (enablePositionsReorder || allowDragPositionToTree)
              "
              :title="positionDragHandleTitle"
              @dragstart.stop="onPositionTreeDragStart($event, data.data)"
              @dragend="onPositionTreeDragEnd"
            >
              <Icon name="grid_icon" size="normal" class="position-role-drag-grid-icon" />
            </div>
          </template>

          <DxColumn
            v-if="!showPositionDragColumn && !allowDragPositionToTree"
            caption=""
            cell-template="dragTemplate"
            :width="44"
            :allow-sorting="false"
            alignment="center"
          />
          <template #dragTemplate>
            <span class="department-card__drag-handle" aria-label="Drag to reorder">
              <DragHandleIcon />
            </span>
          </template>

          <DxColumn
            data-field="name"
            caption="Position"
            cell-template="positionTemplate"
            :min-width="300"
          />
          <template #positionTemplate="{ data }">
            <OrgObjectCard
              type="position"
              size="Medium"
              :icon="true"
              :code="true"
              :code-value="data.data.code ? '#' + data.data.code : ''"
              :name="data.data.name"
              :state="data.data.state === 'DISABLED' ? 'Disabled' : 'Active'"
              :id="data.data.id"
              :allow_hover="false"
              :show-tooltip="true"
              :show-drill-down="false"
              class="department-card__position-object"
            />
          </template>

          <DxColumn
            data-field="occupiedBy"
            caption="Occupied by"
            cell-template="occupiedByTemplate"
            :width="250"
            :allow-sorting="false"
          />
          <template #occupiedByTemplate="{ data }">
            <div v-if="data.data.occupiedBy" class="department-card__employee-cell">
              <EmployeeCard
                :first-name="getOccupiedByProps(data.data.occupiedBy).firstName"
                :last-name="getOccupiedByProps(data.data.occupiedBy).lastName"
                :position="data.data.occupiedBy.title"
                :photo-url="data.data.occupiedBy.avatar"
                :hide-availability-selector="true"
                :show-availability="false"
                :show-importance-selector="false"
                :is-hover-enabled="false"
                :show-link-underline="false"
                text-column-max-width="140px"
              />
            </div>
          </template>

          <DxColumn
            v-if="showPositionRemove"
            caption=""
            cell-template="removeTemplate"
            :width="130"
            alignment="center"
            :allow-sorting="false"
          />
          <template #removeTemplate="{ data }">
            <button
              v-if="!isSubDepartmentRow(data.data)"
              class="department-card__btn-primary"
              @click="$emit('remove-position', data.data)"
            >
              Remove
            </button>
          </template>

          <DxColumn
            data-field="status"
            caption="Status"
            cell-template="statusTemplate"
            :width="110"
            alignment="center"
          />
          <template #statusTemplate="{ data }">
            <OrgObjectStatusBadge :status="data.data.status.toLowerCase()" />
          </template>

          <DxColumn
            caption=""
            cell-template="chatterTemplate"
            :width="72"
            alignment="center"
            :allow-sorting="false"
          />
          <template #chatterTemplate="{ data }">
            <div
              class="department-card__chatter-cell"
              @click="$emit('open-position-chatter', data.data)"
            >
              <ChatterIcon />
            </div>
          </template>

          <DxColumn
            caption=""
            cell-template="menuTemplate"
            :width="100"
            alignment="center"
            :allow-sorting="false"
          />
          <template #menuTemplate="{ data }">
            <div class="department-card__menu-cell">
              <MenuIcon
                :dropdown-items="resolvePositionMenuItems(data.data)"
                :show-tooltip="false"
                @onClick="(item) => $emit('open-position-menu', { position: data.data, item })"
              />
            </div>
          </template>
        </DxDataGrid>
      </div>

      <ExPagination
        v-if="paginationData.total > paginationData.perPage"
        :pagination="paginationData"
        @onRequest="onPageChange"
      />

      <SearchModal
        :dialog="showFilterDialog"
        filterType="Positions"
        :filterGroups="filterGroups"
        :statusOptions="statusOptions"
        :defaultSelected="appliedFilterKeys"
        :clearSelection="clearSelection"
        @back="onFilterDialogBack"
        @checkedItemsChanged="onFiltersApplied"
      />
    </div>

    <!-- Context tab (idea-MDP-494) -->
    <div
      v-else-if="activeTab === TABS.CONTEXT && showContextTabV2"
      class="department-card__body"
      role="tabpanel"
    >
      <ContextTab
        :state="contextState"
        :context="contextData?.context || null"
        :context-updated-at="contextData?.context_updated_at || null"
        :context-updated-by="contextData?.context_updated_by || null"
        :files="contextFiles"
        :uploading-files="uploadingContextFiles"
        :disabled="isSavingContext"
        :draft="contextDraft"
        :max-length="ENTITY_CONTEXT_MAX_LENGTH"
        :suggesting="isSuggestingContext"
        :suggest-error="contextSuggestError"
        @save="handleContextSave"
        @suggest="onContextSuggest"
        @improve="onContextSuggest"
        @input="setContextDraft"
        @discard="clearContextDraft"
        @upload="handleContextUpload"
        @remove="handleContextRemove"
      />
    </div>

    <ContextIQOverlay
      v-if="contextIQOverlayEnabled"
      :model-value="isContextIQOverlayOpen"
      object-type="department"
      :object-id="departmentId"
      :object-name="name"
      :context="contextOverlaySeed"
      @save="handleContextOverlaySave"
      @close="isContextIQOverlayOpen = false"
    />
  </div>
</template>

<script>
export const DEPARTMENT_CARD_TABS = {
  POSITIONS: "POSITIONS",
  CONTEXT: "CONTEXT",
};

export const ORG_OBJECT_STATUS = {
  ACTIVE: "ACTIVE",
  DRAFT: "DRAFT",
};

const TAB_LABELS = {
  [DEPARTMENT_CARD_TABS.POSITIONS]: "Positions",
  [DEPARTMENT_CARD_TABS.CONTEXT]: "Context",
};
</script>

<script setup>
import { computed, ref, watch, onBeforeUnmount, onMounted, getCurrentInstance } from "vue";
import ContextTab from "@components/ContextTab/ContextTab.vue";
import ContextIQOverlay from "@components/ContextIQOverlay/ContextIQOverlay.vue";
import {
  useContextSuggestion,
  ENTITY_CONTEXT_MAX_LENGTH,
} from "@components/utils/useContextSuggestion";
import {
  LD_FLAG_IDEA_MDP_494,
  LD_FLAG_IDEA_MDP_523,
  LD_FLAG_IDEA_MDP_509,
} from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";
import { DxDataGrid, DxColumn, DxRowDragging, DxPaging, DxPager } from "devextreme-vue/data-grid";
import Icon from "@components/icons/Icon.vue";
import DepartmentPageHeader from "@components/DepartmentPageHeader/DepartmentPageHeader.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import EmployeeHorizontalMenu from "@components/EmployeeHorizontalMenu/EmployeeHorizontalMenu.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import EmployeeCard from "@components/organizations/employees/EmployeeCard/EmployeeCard.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import DragHandleIcon from "./icons/DragHandleIcon.vue";

const TABS = DEPARTMENT_CARD_TABS;

/** ER-10044 / ER-9857 — readiness scope toggle: switch the header readiness value
 * between the whole subtree and just this department. */
const DEPARTMENT_READINESS_SCOPE = {
  SUBTREE: "subtree",
  SELF: "self",
};

const instance = getCurrentInstance();
/** LaunchDarkly idea-MDP-450 — wider unassigned-positions search so placeholder stays readable. */
const ideaMDP450 = computed(() =>
  Boolean(instance?.proxy?.$store?.getters?.["featureFlags/ideaMDP450"])
);

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  code: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    default: ORG_OBJECT_STATUS.ACTIVE,
    validator: (v) => Object.values(ORG_OBJECT_STATUS).includes(v),
  },
  readiness: {
    type: Object,
    default: null,
  },
  /** ER-10044 / ER-9857 — current readiness of this department only (no sub-departments). */
  currentReadiness: {
    type: Number,
    default: null,
  },
  /** ER-10044 / ER-9857 — current readiness including all sub-departments. */
  currentReadinessSubtree: {
    type: Number,
    default: null,
  },
  chatterUnreadCount: {
    type: Number,
    default: 0,
  },
  menuItems: {
    type: Array,
    default: () => [],
  },
  positionMenuItems: {
    type: Array,
    default: () => [],
  },
  getPositionMenuItems: {
    type: Function,
    default: null,
  },
  activeTab: {
    type: String,
    default: DEPARTMENT_CARD_TABS.POSITIONS,
    validator: (v) => Object.values(DEPARTMENT_CARD_TABS).includes(v),
  },
  positionsCount: {
    type: Number,
    default: 0,
  },
  /** Saved department context + audit fields from GET department detail. */
  contextData: {
    type: Object,
    default: null,
  },
  /** ER-10219: department id passed to the suggest-context endpoint. */
  departmentId: {
    type: [Number, String],
    default: null,
  },
  contextFiles: {
    type: Array,
    default: () => [],
  },
  uploadingContextFiles: {
    type: Array,
    default: () => [],
  },
  isSavingContext: {
    type: Boolean,
    default: false,
  },
  positions: {
    type: Array,
    default: () => [],
  },
  filterChips: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => [],
  },
  statusOptions: {
    type: Array,
    default: () => ["draft", "active", "retired", "inactive"],
  },
  searchPlaceholder: {
    type: String,
    default: "Search position by name or code",
  },
  page: {
    type: Number,
    default: 1,
  },
  pageCount: {
    type: Number,
    default: 1,
  },
  perPage: {
    type: Number,
    default: 10,
  },
  allowDragPositionToTree: {
    type: Boolean,
    default: false,
  },
  enablePositionsReorder: {
    type: Boolean,
    default: false,
  },
  showPositionRemove: {
    type: Boolean,
    default: true,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  hideFilters: {
    type: Boolean,
    default: false,
  },
  sourceDepartmentId: {
    type: [Number, String],
    default: null,
  },
  onPositionDragStartCallback: {
    type: Function,
    default: null,
  },
  onPositionDragEndCallback: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits([
  "update:activeTab",
  "update:page",
  "update:search",
  "remove-filter",
  "open-more-filters",
  "remove-position",
  "open-position-chatter",
  "open-position-menu",
  "open-department-chatter",
  "open-department-menu",
  "reorder",
  "clear-filters",
  "filters-applied",
  "context-save",
  "context-upload",
  "context-remove",
  "back-to-list",
  "scope-change",
]);

/** Department detail breadcrumb trail — matches the Org Object detail pages (Role,
 * Responsibility, Job Family, Job Category): "Departments" links back to the list,
 * "Department" is the static, non-clickable entity-type crumb. */
const departmentBreadcrumbs = computed(() => [
  {
    text: "Departments",
    href: "/organization/departments",
    disabled: false,
  },
  {
    text: "Department",
    disabled: true,
  },
]);

/** ER-11885 — the Department detail is an in-component drill-down rendered at the
 * same `/organization/departments` route as the list, so ExBreadcrumbs' `navigateTo`
 * skips `$router.push` (fullPath already equals the crumb href). Emit instead, so the
 * parent resets the Departments table back to the list view. */
function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb?.disabled) return;
  emit("back-to-list");
}

/** idea-MDP-494: render Context tab when enabled. Starts false until LD resolves. */
const showContextTabV2 = ref(false);
/** idea-MDP-523: numeric Context tab badge (files + context text). */
const showContextCountBadge = ref(false);
/** idea-MDP-523 (ER-10140): Suggest / Improve open the IQ Overlay instead of the inline draft. */
const contextIQOverlayEnabled = ref(false);
const isContextIQOverlayOpen = ref(false);
/** idea-MDP-509: render the DepartmentCard readiness scope toggle. Off by default. */
const showReadinessScopeToggle = ref(false);
/** ER-10044 — selected readiness scope; defaults to subtree. */
const activeReadinessScope = ref(DEPARTMENT_READINESS_SCOPE.SUBTREE);

onMounted(async () => {
  showContextTabV2.value =
    (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_494, false)) === true;
  showContextCountBadge.value =
    (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_523, false)) === true;
  contextIQOverlayEnabled.value = showContextCountBadge.value;
  showReadinessScopeToggle.value =
    (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_509, false)) === true;
  if (showReadinessScopeToggle.value) {
    emit("scope-change", activeReadinessScope.value);
  }
});

/** ER-10044 — the header readiness value for the selected scope. When the scoped
 * current-readiness prop for the active scope is absent (parent not wired yet),
 * fall back to the existing avg-readiness object so nothing regresses. */
const displayedReadiness = computed(() => {
  const scopedPercent =
    activeReadinessScope.value === DEPARTMENT_READINESS_SCOPE.SUBTREE
      ? props.currentReadinessSubtree
      : props.currentReadiness;
  if (scopedPercent != null) {
    return { percent: scopedPercent, changePercent: null };
  }
  return props.readiness;
});

function setReadinessScope(scope) {
  if (activeReadinessScope.value === scope) return;
  activeReadinessScope.value = scope;
  emit("scope-change", scope);
}

watch(
  () => props.code,
  () => {
    setReadinessScope(DEPARTMENT_READINESS_SCOPE.SUBTREE);
  }
);

/** The scope can list positions of sub-departments; those rows are read-only because
 * Remove and move act on the position's own department, not the selected one. */
function isSubDepartmentRow(row) {
  if (props.sourceDepartmentId == null || row?.departmentId == null) return false;
  return String(row.departmentId) !== String(props.sourceDepartmentId);
}

const contextBadgeCount = computed(() => {
  const filesCount = props.contextFiles?.length ?? 0;
  const hasContext = Boolean((props.contextData?.context || "").trim());
  return filesCount + (hasContext ? 1 : 0);
});

const contextState = computed(() =>
  (props.contextData?.context || "").trim() ? "filled" : "empty"
);

/** ER-10219: IQ "Suggest" for the department Context field — unsaved draft. */
const {
  draft: contextDraft,
  isSuggesting: isSuggestingContext,
  suggestError: contextSuggestError,
  suggest: suggestContextText,
  setDraft: setContextDraft,
  clearDraft: clearContextDraft,
} = useContextSuggestion("department");

/** The text the IQ Overlay opens on: what the editor currently shows (unsaved draft, else saved). */
const contextOverlaySeed = computed(
  () => contextDraft.value ?? props.contextData?.context ?? ""
);

function onContextSuggest() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
    return undefined;
  }
  return suggestContextText(props.departmentId);
}

/** Clear the draft once the parent's refresh shows the saved context. */
watch(
  () => props.contextData?.context,
  (savedContext) => {
    if (contextDraft.value !== null && (savedContext || "") === contextDraft.value) {
      clearContextDraft();
    }
  }
);

function handleContextSave(value) {
  emit("context-save", value);
}

/** Keep the overlay's text as the editor draft until the parent's refresh shows it saved. */
function handleContextOverlaySave(value) {
  setContextDraft(value);
  handleContextSave(value);
}

function handleContextUpload(fileList) {
  emit("context-upload", fileList);
}

function handleContextRemove(fileId) {
  emit("context-remove", fileId);
}

watch(showContextTabV2, (enabled) => {
  if (!enabled && props.activeTab === TABS.CONTEXT) {
    emit("update:activeTab", TABS.POSITIONS);
  }
});

const hasSearch = ref(false);
const searchValue = ref("");
const showFilterDialog = ref(false);
const clearSelection = ref("");
const positionsGridWrapperRef = ref(null);

const POSITIONS_GRID_ROOT_STYLE = {
  width: "max-content",
  minWidth: "100%",
};

const positionsGridElementAttr = {
  class: "department-card-grid",
  style: "width: max-content; min-width: 100%;",
};
const gridPositions = ref([]);
let positionDragGhostRowEl = null;
let positionDragGhostEl = null;
let positionDragGhostLayout = null;
let positionDragUnifiedDragoverCleanup = null;
let positionDragUiMode = null;
let positionReorderSourceRowIndex = null;
let positionReorderDropRowsCache = null;
let positionReorderDropIndicator = null;
let positionReorderDropInsertIndex = null;
let rowsViewPositionDnDCleanup = null;
let positionTreeDragSessionActive = false;
let positionTreeDragEndListenerCleanup = null;

function scrubPositionDragVisuals() {
  resetStuckPositionDragRowStyles();
  clearPositionDragGhost();
  document.querySelector("[data-position-department-drag-ghost]")?.remove();
  document.querySelector("[data-position-reorder-drop-indicator]")?.remove();
}

function finishPositionTreeDrag({ notifyParent = true } = {}) {
  const wasActive = positionTreeDragSessionActive;
  positionTreeDragSessionActive = false;
  if (typeof positionTreeDragEndListenerCleanup === "function") {
    positionTreeDragEndListenerCleanup();
    positionTreeDragEndListenerCleanup = null;
  }
  scrubPositionDragVisuals();
  if (notifyParent && wasActive) {
    props.onPositionDragEndCallback?.();
  }
}

function attachPositionTreeDragEndListener() {
  if (typeof positionTreeDragEndListenerCleanup === "function") {
    positionTreeDragEndListenerCleanup();
  }

  const onDocumentDragEnd = () => finishPositionTreeDrag();
  const onDocumentDrop = () => {
    window.requestAnimationFrame(() => {
      if (positionTreeDragSessionActive) {
        finishPositionTreeDrag();
      }
    });
  };

  document.addEventListener("dragend", onDocumentDragEnd);
  document.addEventListener("drop", onDocumentDrop, true);

  positionTreeDragEndListenerCleanup = () => {
    document.removeEventListener("dragend", onDocumentDragEnd);
    document.removeEventListener("drop", onDocumentDrop, true);
    positionTreeDragEndListenerCleanup = null;
  };
}

watch(
  () => props.positions,
  (next) => {
    gridPositions.value = Array.isArray(next) ? [...next] : [];
  },
  { immediate: true, deep: true }
);

watch(
  () => [props.enablePositionsReorder, props.allowDragPositionToTree],
  () => {
    scheduleRowsViewPositionDnDAttach();
  }
);

const showPositionDragColumn = computed(
  () => props.enablePositionsReorder || props.allowDragPositionToTree
);

const useUnifiedPositionDragHandle = computed(
  () => props.enablePositionsReorder && props.allowDragPositionToTree
);

const positionDragHandleTitle = computed(() => {
  const reorder = props.enablePositionsReorder;
  const tree = props.allowDragPositionToTree;
  if (reorder && tree) {
    return "Drag to reorder, or drag to the department tree to attach";
  }
  if (reorder) return "Drag to reorder positions";
  if (tree) return "Drag to attach this position to another department in the tree";
  return "";
});

const activeTabLabel = computed(() => TAB_LABELS[props.activeTab] || TAB_LABELS[TABS.POSITIONS]);

const tabMenuItems = computed(() => {
  const items = [
    {
      label: TAB_LABELS[TABS.POSITIONS],
      enabled: true,
      badge: props.positionsCount,
    },
  ];

  if (showContextTabV2.value) {
    const contextTab = {
      label: TAB_LABELS[TABS.CONTEXT],
      enabled: true,
    };
    if (showContextCountBadge.value) {
      contextTab.badge = Math.abs(Number(contextBadgeCount.value)) || 0;
    }
    items.push(contextTab);
  }

  return items;
});

const filterChipLabels = computed(() => props.filterChips.map((chip) => chip.label));

const appliedFilterKeys = computed(() => props.filterChips.map((chip) => chip.key));

/** Unassigned panel hides the Filter button; reclaim that header space for the search input (idea-MDP-450). */
const wideUnassignedSearch = computed(() => ideaMDP450.value && props.hideFilters);

const clearLabel = computed(() => {
  const hasFilters = props.filterChips.length > 0;
  if (hasSearch.value && hasFilters) return "Clear All";
  if (hasFilters) return "Clear Filters";
  if (hasSearch.value) return "Clear Search";
  return "Clear";
});

const paginationData = computed(() => {
  const backendTotal =
    props.positionsCount > 0 ? props.positionsCount : props.pageCount * props.perPage;

  return {
    page: props.page,
    total: backendTotal,
    perPage: props.perPage,
  };
});

const noRecordFoundSpecificMessage = computed(() => {
  if (props.filterChips.length > 0 || hasSearch.value) {
    return "No positions found";
  }
  if (props.hideHeader) {
    return "No positions without department";
  }
  return "No positions in this department yet.";
});

const noRecordFoundDescription = computed(() => {
  if (props.filterChips.length === 0 && !hasSearch.value) {
    return props.hideHeader
      ? "This is exactly how it should be"
      : "Add positions to this department to get started.";
  }
  if (hasSearch.value && props.filterChips.length > 0) {
    return "Clear all filters and search to view positions.";
  }
  if (hasSearch.value) {
    return "Your search did not match any positions. Please try again.";
  }
  return "Your filter did not match any positions. Please try again.";
});

function onTabSelected(label) {
  const tab = Object.entries(TAB_LABELS).find(([, tabLabel]) => tabLabel === label)?.[0];
  if (tab) {
    emit("update:activeTab", tab);
  }
}

function onSearch(value) {
  searchValue.value = value;
  hasSearch.value = value.length > 0;
  emit("update:search", value);
}

function onFilterClick() {
  showFilterDialog.value = true;
  emit("open-more-filters");
}

function onFiltersApplied(items) {
  const chips = (items || [])
    .filter((item) => item && (item.value != null || item.label != null))
    .map((item) => ({
      key: item.value ?? item.label,
      label: item.label ?? String(item.value),
    }));
  showFilterDialog.value = false;
  clearSelection.value = "";
  emit("filters-applied", chips);
}

function onFilterDialogBack() {
  showFilterDialog.value = false;
  clearSelection.value = "";
}

function onRemoveFilter(label) {
  const chip = props.filterChips.find((item) => item.label === label);
  if (chip) {
    clearSelection.value = label;
    emit("remove-filter", chip.key);
  }
}

function onClearFilters() {
  hasSearch.value = false;
  searchValue.value = "";
  emit("clear-filters");
}

function onPageChange(data) {
  emit("update:page", data.page);
}

function onReorder(e) {
  emit("reorder", { from: e.fromIndex, to: e.toIndex });
}

function resetStuckPositionDragRowStyles() {
  positionsGridWrapperRef.value
    ?.querySelectorAll?.(".position-role-reorder-drag-source")
    ?.forEach((row) => row.classList.remove("position-role-reorder-drag-source"));
}

function removePositionDragGhostElement() {
  positionDragGhostEl?.remove();
  positionDragGhostEl = null;
  document.querySelector("[data-position-department-drag-ghost]")?.remove();
  positionDragGhostLayout = null;
}

function clearPositionDragGhost() {
  if (typeof positionDragUnifiedDragoverCleanup === "function") {
    positionDragUnifiedDragoverCleanup();
    positionDragUnifiedDragoverCleanup = null;
  }
  removePositionDragGhostElement();
  positionDragGhostRowEl = null;
  positionDragUiMode = null;
  positionReorderDropRowsCache = null;
  positionReorderSourceRowIndex = null;
  clearPositionReorderDropIndicator();
}

function clearPositionReorderDropIndicator() {
  if (positionReorderDropIndicator?.parentNode) {
    positionReorderDropIndicator.remove();
  }
  positionReorderDropIndicator = null;
  positionReorderDropInsertIndex = null;
}

function isPositionReorderDragActive() {
  return Boolean(
    props.enablePositionsReorder && positionDragGhostRowEl && positionDragUiMode !== "attach"
  );
}

function isPointerOverPaneLeft(clientX, clientY) {
  const layout = positionsGridWrapperRef.value?.closest?.(".pane-view-layout");
  const leftPane = layout?.querySelector?.(".pane-view-left");
  if (!leftPane) return false;
  const lr = leftPane.getBoundingClientRect();
  return clientX >= lr.left && clientX <= lr.right && clientY >= lr.top && clientY <= lr.bottom;
}

function resolveActivePositionDragUiMode(clientX, clientY) {
  if (props.allowDragPositionToTree && isPointerOverPaneLeft(clientX, clientY)) {
    return "attach";
  }
  return "reorder";
}

function isPointerOverPositionReorderZone(clientX, clientY) {
  if (isPointerOverPaneLeft(clientX, clientY)) {
    return false;
  }
  const rowsView = positionsGridWrapperRef.value?.querySelector?.(".dx-datagrid-rowsview");
  if (rowsView) {
    const r = rowsView.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  }
  const paneRight = positionsGridWrapperRef.value?.closest?.(".pane-view-right");
  if (paneRight) {
    const r = paneRight.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  }
  const grid = positionsGridWrapperRef.value?.querySelector?.(".dx-datagrid");
  if (!grid) return true;
  const r = grid.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

function getPositionReorderDropRows(rowsView) {
  if (positionReorderDropRowsCache) return positionReorderDropRowsCache;
  const tbody = rowsView?.querySelector?.("tbody");
  if (!tbody) return [];
  positionReorderDropRowsCache = Array.from(tbody.querySelectorAll("tr.dx-data-row"));
  return positionReorderDropRowsCache;
}

function resolvePositionReorderInsertIndex(clientY, rows) {
  if (!rows?.length) return 0;
  for (let i = 0; i < rows.length; i++) {
    const rect = rows[i].getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    if (clientY < mid) return i;
  }
  return rows.length;
}

function resolvePositionReorderTargetIndex(fromIndex, insertIndex) {
  if (fromIndex == null || insertIndex == null || fromIndex < 0 || insertIndex < 0) {
    return insertIndex;
  }
  let toIndex = insertIndex;
  if (fromIndex < insertIndex) toIndex -= 1;
  return toIndex;
}

function updatePositionReorderDropIndicator(ev, rowsView) {
  if (!isPositionReorderDragActive() || !rowsView) {
    clearPositionReorderDropIndicator();
    return;
  }
  const rows = getPositionReorderDropRows(rowsView);
  if (!rows.length) {
    clearPositionReorderDropIndicator();
    return;
  }
  const insertIndex = resolvePositionReorderInsertIndex(ev.clientY, rows);
  positionReorderDropInsertIndex = insertIndex;
  const sourceIndex = positionReorderSourceRowIndex;
  if (
    sourceIndex != null &&
    sourceIndex >= 0 &&
    (insertIndex === sourceIndex || insertIndex === sourceIndex + 1)
  ) {
    clearPositionReorderDropIndicator();
    return;
  }
  let lineTop;
  if (insertIndex <= 0) {
    lineTop = rows[0].getBoundingClientRect().top;
  } else if (insertIndex >= rows.length) {
    lineTop = rows[rows.length - 1].getBoundingClientRect().bottom;
  } else {
    lineTop = rows[insertIndex].getBoundingClientRect().top;
  }
  const rowsViewRect = rowsView.getBoundingClientRect();
  if (!positionReorderDropIndicator) {
    positionReorderDropIndicator = document.createElement("div");
    positionReorderDropIndicator.className = "position-role-reorder-drop-indicator";
    positionReorderDropIndicator.setAttribute("data-position-reorder-drop-indicator", "");
    document.body.appendChild(positionReorderDropIndicator);
  }
  Object.assign(positionReorderDropIndicator.style, {
    position: "fixed",
    left: `${rowsViewRect.left}px`,
    top: `${lineTop}px`,
    width: `${rowsViewRect.width}px`,
  });
}

function applyPositionDragUiMode(mode, clientX, clientY) {
  if (!useUnifiedPositionDragHandle.value || !positionDragGhostRowEl) return;
  if (positionDragUiMode === mode) return;
  positionDragUiMode = mode;
  removePositionDragGhostElement();
  createPositionDragGhost({ clientX, clientY }, positionDragGhostRowEl, mode);
  if (mode === "attach") {
    clearPositionReorderDropIndicator();
  }
}

function handleUnifiedPositionDragPointerMove(ev) {
  updatePositionDragGhostPosition(ev.clientX, ev.clientY);
  const rowsView = positionsGridWrapperRef.value?.querySelector?.(".dx-datagrid-rowsview");
  if (useUnifiedPositionDragHandle.value || props.allowDragPositionToTree) {
    const mode = resolveActivePositionDragUiMode(ev.clientX, ev.clientY);
    applyPositionDragUiMode(mode, ev.clientX, ev.clientY);
    if (mode === "reorder" && rowsView && props.enablePositionsReorder) {
      updatePositionReorderDropIndicator(ev, rowsView);
    } else if (mode === "attach") {
      clearPositionReorderDropIndicator();
    }
  } else if (props.enablePositionsReorder && rowsView) {
    updatePositionReorderDropIndicator(ev, rowsView);
  }
}

function setEmptyNativeDragImage(event) {
  if (!event?.dataTransfer || typeof event.dataTransfer.setDragImage !== "function") return;
  const empty = document.createElement("div");
  empty.style.cssText = "width:1px;height:1px;position:fixed;left:-9999px;top:0;";
  document.body.appendChild(empty);
  event.dataTransfer.setDragImage(empty, 0, 0);
  setTimeout(() => {
    if (empty.parentNode) empty.remove();
  }, 0);
}

function updatePositionDragGhostPosition(clientX, clientY) {
  if (!positionDragGhostEl || !positionDragGhostLayout) return;
  const x = clientX - positionDragGhostLayout.offsetX - positionDragGhostLayout.cursorSpaceLeft;
  const y = clientY - positionDragGhostLayout.offsetY;
  positionDragGhostEl.style.transform = `translate3d(${x}px, ${y}px, 0)`;
}

function attachPositionDragUnifiedDragoverListener() {
  if (typeof positionDragUnifiedDragoverCleanup === "function") return;
  let rafId = 0;
  let lastEvent = null;
  const flush = () => {
    rafId = 0;
    if (!lastEvent || !positionDragGhostRowEl || !positionTreeDragSessionActive) return;
    handleUnifiedPositionDragPointerMove(lastEvent);
  };
  const onDragover = (e) => {
    if (!positionDragGhostRowEl) return;
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = positionDragUiMode === "attach" ? "copy" : "move";
    }
    lastEvent = e;
    if (!rafId) rafId = requestAnimationFrame(flush);
  };
  document.addEventListener("dragover", onDragover);
  positionDragUnifiedDragoverCleanup = () => {
    if (rafId) cancelAnimationFrame(rafId);
    document.removeEventListener("dragover", onDragover);
  };
}

function syncPositionDragGhostCursorVars() {
  if (!positionDragGhostEl) return;
  const rootStyles = getComputedStyle(document.documentElement);
  const fill = rootStyles.getPropertyValue("--position-tree-drag-hand-fill").trim();
  const labelColor = rootStyles.getPropertyValue("--position-tree-drag-label-color").trim();
  if (fill) {
    positionDragGhostEl.style.setProperty("--position-tree-drag-hand-fill", fill);
    positionDragGhostEl.querySelectorAll("[data-position-drag-ghost-hand-fill]").forEach((path) => {
      path.setAttribute("fill", fill);
    });
  }
  if (labelColor) {
    positionDragGhostEl.style.setProperty("--position-tree-drag-label-color", labelColor);
    const label = positionDragGhostEl.querySelector("[data-position-drag-ghost-label]");
    if (label instanceof HTMLElement) {
      label.style.color = labelColor;
    }
  }
}

function createPositionDragGhost(pointerEvent, rowEl, variant = "attach") {
  if (!rowEl || !pointerEvent) return;
  const isAttach = variant === "attach";
  const offsetX = 28;
  const offsetY = 22;
  const cursorSpaceLeft = isAttach ? 72 : 0;

  const rowClone = rowEl.cloneNode(true);
  rowClone.querySelector(".position-role-tree-drag-handle")?.removeAttribute("draggable");
  rowClone.querySelectorAll(".drag-handle-cell-wrapper").forEach((el) => el.remove());
  const sourceCells = rowEl.querySelectorAll("td");
  const cloneCells = rowClone.querySelectorAll("td");
  for (let i = 0; i < sourceCells.length && i < cloneCells.length; i++) {
    const w = sourceCells[i].offsetWidth;
    if (w) cloneCells[i].style.width = `${w}px`;
    cloneCells[i].style.minWidth = `${sourceCells[i].offsetWidth || 0}px`;
  }
  const leadCols = showPositionDragColumn.value ? 1 : 0;
  for (let c = 0; c < leadCols && c < cloneCells.length; c++) {
    if (cloneCells[c]) {
      cloneCells[c].style.width = "0";
      cloneCells[c].style.minWidth = "0";
      cloneCells[c].style.paddingLeft = "0";
      cloneCells[c].style.paddingRight = "0";
    }
  }
  const positionColIdx = leadCols;
  const srcPositionW = sourceCells[positionColIdx]?.offsetWidth || 0;
  const targetPositionW =
    cloneCells[positionColIdx] && sourceCells[positionColIdx] ? Math.max(320, srcPositionW) : 0;
  const positionExtra =
    cloneCells[positionColIdx] && sourceCells[positionColIdx]
      ? Math.max(0, targetPositionW - srcPositionW)
      : 0;
  if (cloneCells[positionColIdx] && targetPositionW > 0) {
    cloneCells[positionColIdx].style.minWidth = `${targetPositionW}px`;
    cloneCells[positionColIdx].style.width = `${targetPositionW}px`;
    cloneCells[positionColIdx].style.maxWidth = "none";
    cloneCells[positionColIdx].style.overflow = "visible";
    cloneCells[positionColIdx].querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) el.style.maxWidth = "none";
    });
  }

  const rowsView = positionsGridWrapperRef.value?.querySelector?.(".dx-datagrid-rowsview");
  const rowsViewWidth = rowsView?.getBoundingClientRect()?.width || 0;
  const measuredRowW = rowEl.getBoundingClientRect().width || rowEl.offsetWidth;
  let tableWidth = Math.max(measuredRowW + positionExtra, rowsViewWidth, 1);

  let assignedWidth = 0;
  for (let c = 0; c < cloneCells.length; c++) {
    if (c < leadCols) continue;
    assignedWidth += parseInt(cloneCells[c].style.width, 10) || sourceCells[c]?.offsetWidth || 0;
  }
  const widthDeficit = tableWidth - assignedWidth;
  if (widthDeficit > 0 && cloneCells[positionColIdx]) {
    const currentPositionW =
      parseInt(cloneCells[positionColIdx].style.width, 10) ||
      sourceCells[positionColIdx]?.offsetWidth ||
      0;
    const expandedPositionW = currentPositionW + widthDeficit;
    cloneCells[positionColIdx].style.minWidth = `${expandedPositionW}px`;
    cloneCells[positionColIdx].style.width = `${expandedPositionW}px`;
    cloneCells[positionColIdx].style.maxWidth = "none";
    cloneCells[positionColIdx].style.overflow = "visible";
    cloneCells[positionColIdx].querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) el.style.maxWidth = "none";
    });
  }

  const table = document.createElement("table");
  table.setAttribute("role", "presentation");
  table.className = "dx-datagrid-table";
  table.style.width = `${tableWidth}px`;
  table.style.minWidth = `${tableWidth}px`;
  table.style.borderCollapse = "collapse";
  table.style.tableLayout = "fixed";
  table.style.margin = "0";
  table.appendChild(rowClone);

  const ghost = document.createElement("div");
  ghost.className = [
    "role-position-drag-ghost",
    "department-card-position-drag",
    "roles-table-wrapper",
    isAttach ? "role-position-drag-ghost--attach" : "role-position-drag-ghost--reorder",
  ].join(" ");
  ghost.setAttribute("data-position-department-drag-ghost", "");
  Object.assign(ghost.style, {
    position: "fixed",
    left: "0",
    top: "0",
    width: `${tableWidth + cursorSpaceLeft}px`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    margin: "0",
    padding: isAttach ? "8px 10px 8px 12px" : "8px 12px 8px 18px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    background: "#fff",
    zIndex: "10050",
    pointerEvents: "none",
    overflow: "hidden",
    willChange: "transform",
  });
  positionDragGhostLayout = { offsetX, offsetY, cursorSpaceLeft };
  positionDragGhostEl = ghost;

  if (isAttach && cursorSpaceLeft > 0) {
    const handColumn = document.createElement("div");
    handColumn.setAttribute("aria-hidden", "true");
    handColumn.innerHTML = `
    <div style="display:inline-flex;flex-direction:column;align-items:center;gap:8px;color:var(--position-tree-drag-label-color, #101828);font-family:Inter, sans-serif;font-size:14px;font-weight:500;line-height:20px;white-space:nowrap;">
      <svg xmlns="http://www.w3.org/2000/svg" width="31" height="29" viewBox="0 0 31 29" fill="none" style="display:block;">
        <g filter="url(#position-department-drag-ghost-hand-shadow)">
          <path
            data-position-drag-ghost-hand-fill
            d="M7.49175 2.30967C8.45175 1.94967 10.3517 2.16967 10.8517 3.24967C11.3517 4.32967 11.6517 5.72967 11.6718 5.38967C11.6335 4.35626 11.7208 3.32203 11.9317 2.30967C12.154 1.6613 12.6634 1.15197 13.3117 0.929667C13.9064 0.741609 14.5377 0.700435 15.1517 0.809667C15.7726 0.937463 16.3224 1.29452 16.6917 1.80967C17.1596 2.97593 17.4235 4.21397 17.4717 5.46967C17.5215 4.39818 17.7029 3.33687 18.0118 2.30967C18.346 1.83877 18.834 1.49925 19.3918 1.34967C20.0529 1.22878 20.7306 1.22878 21.3918 1.34967C21.9345 1.52978 22.4091 1.87178 22.7518 2.32967C23.1765 3.38997 23.433 4.51016 23.5118 5.64967C23.5118 5.92967 23.6518 4.86967 24.0918 4.16967C24.4452 3.12033 25.5824 2.5562 26.6318 2.90967C27.6811 3.26313 28.2452 4.40033 27.8918 5.44967C27.8918 6.74967 27.8918 6.68967 27.8918 7.56967C27.8918 8.44967 27.8918 9.22967 27.8918 9.96967C27.8197 11.1401 27.6592 12.3034 27.4118 13.4497C27.0646 14.4643 26.5801 15.4266 25.9718 16.3097C25.0009 17.3898 24.1985 18.6102 23.5918 19.9297C23.4437 20.5857 23.3766 21.2573 23.3918 21.9297C23.3897 22.5509 23.4705 23.1697 23.6318 23.7697C22.8141 23.857 21.9894 23.857 21.1718 23.7697C20.3918 23.6497 19.4318 22.0897 19.1718 21.6097C19.0431 21.3519 18.7798 21.1891 18.4918 21.1891C18.2037 21.1891 17.9404 21.3519 17.8117 21.6097C17.3717 22.3697 16.3917 23.7497 15.8117 23.8297C14.4717 23.9897 11.6917 23.8297 9.53175 23.8297C9.53175 23.8297 9.91175 21.8297 9.07175 21.1097C8.23175 20.3897 7.41175 19.5497 6.79175 18.9897L5.13175 17.1497C3.96113 16.063 3.10451 14.6814 2.65175 13.1497C2.23175 11.2697 2.27175 10.3697 2.65175 9.60967C3.03933 8.98208 3.64453 8.51927 4.35175 8.30967C4.93929 8.20308 5.54411 8.24432 6.11175 8.42967C6.50428 8.594 6.84353 8.86401 7.09175 9.20967C7.55175 9.82967 7.71175 10.1297 7.51175 9.44967C7.31175 8.76967 6.87175 8.26967 6.65175 7.44967C6.22345 6.48126 5.96631 5.44593 5.89175 4.38967C5.93264 3.42814 6.57288 2.59583 7.49175 2.30967Z"
            fill="var(--position-tree-drag-hand-fill, #ffffff)"
            stroke="black"
            stroke-width="1.5"
            stroke-linejoin="round"
          />
          <path data-position-drag-ghost-hand-fill d="M22.9922 17.6596V10.756C22.9922 10.3428 22.6564 10.0078 22.2422 10.0078C21.828 10.0078 21.4922 10.3428 21.4922 10.756V17.6596C21.4922 18.0728 21.828 18.4078 22.2422 18.4078C22.6564 18.4078 22.9922 18.0728 22.9922 17.6596Z" fill="var(--position-tree-drag-hand-fill, #000000)"/>
          <path data-position-drag-ghost-hand-fill d="M19.0322 17.6571L18.9922 10.75C18.9898 10.3377 18.6521 10.0054 18.2379 10.0078C17.8237 10.0102 17.4898 10.3463 17.4922 10.7586L17.5322 17.6657C17.5346 18.0779 17.8723 18.4102 18.2865 18.4078C18.7007 18.4054 19.0346 18.0693 19.0322 17.6571Z" fill="var(--position-tree-drag-hand-fill, #000000)"/>
          <path data-position-drag-ghost-hand-fill d="M13.4922 10.7676L13.5322 17.6568C13.5346 18.074 13.8724 18.4102 14.2866 18.4078C14.7008 18.4054 15.0346 18.0652 15.0322 17.648L14.9922 10.7588C14.9898 10.3416 14.652 10.0054 14.2378 10.0078C13.8236 10.0103 13.4898 10.3504 13.4922 10.7676Z" fill="var(--position-tree-drag-hand-fill, #000000)"/>
        </g>
        <defs>
          <filter id="position-department-drag-ghost-hand-shadow" x="0" y="0" width="31" height="29" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feDropShadow dx="0" dy="2" stdDeviation="0.8" flood-opacity="0.5"/>
          </filter>
        </defs>
      </svg>
      <span data-position-drag-ghost-label>Attach</span>
    </div>
  `;
    Object.assign(handColumn.style, {
      flex: `0 0 ${cursorSpaceLeft}px`,
      width: `${cursorSpaceLeft}px`,
      minWidth: `${cursorSpaceLeft}px`,
      alignSelf: "stretch",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
    });
    ghost.appendChild(handColumn);
  }
  ghost.appendChild(table);
  document.body.appendChild(ghost);
  updatePositionDragGhostPosition(pointerEvent.clientX, pointerEvent.clientY);
  if (isAttach) {
    syncPositionDragGhostCursorVars();
  }
}

function detachRowsViewPositionDnDListeners() {
  if (typeof rowsViewPositionDnDCleanup === "function") {
    rowsViewPositionDnDCleanup();
    rowsViewPositionDnDCleanup = null;
  }
  clearPositionReorderDropIndicator();
}

function handlePositionsGridRowDrop(ev) {
  if (!props.enablePositionsReorder) return;
  const raw = ev.dataTransfer?.getData("application/x-exiqtive-position-department");
  if (!raw) return;
  let payload;
  try {
    payload = JSON.parse(raw);
  } catch {
    finishPositionTreeDrag();
    return;
  }
  const fromIndex = payload.sourceRowIndex;
  if (fromIndex == null || fromIndex < 0) {
    finishPositionTreeDrag();
    return;
  }
  const rowsView = positionsGridWrapperRef.value?.querySelector?.(".dx-datagrid-rowsview");
  let toIndex = positionReorderDropInsertIndex;
  if (toIndex == null && rowsView) {
    const rows = getPositionReorderDropRows(rowsView);
    toIndex = resolvePositionReorderInsertIndex(ev.clientY, rows);
  }
  if (toIndex == null) {
    const row = ev.target?.closest?.("tr.dx-data-row");
    if (!row || !positionsGridWrapperRef.value?.contains(row)) {
      finishPositionTreeDrag();
      return;
    }
    const tbody = row.closest("tbody");
    if (!tbody) {
      finishPositionTreeDrag();
      return;
    }
    const allRows = Array.from(tbody.querySelectorAll("tr.dx-data-row"));
    toIndex = allRows.indexOf(row);
  } else {
    toIndex = resolvePositionReorderTargetIndex(fromIndex, toIndex);
  }
  if (toIndex < 0 || fromIndex === toIndex) {
    finishPositionTreeDrag();
    return;
  }
  ev.preventDefault();
  ev.stopPropagation();
  const itemMoving = gridPositions.value.splice(fromIndex, 1)[0];
  gridPositions.value.splice(toIndex, 0, itemMoving);
  emit("reorder", { from: fromIndex, to: toIndex, data: [...gridPositions.value] });
  finishPositionTreeDrag();
}

function scheduleRowsViewPositionDnDAttach() {
  const rowsView = positionsGridWrapperRef.value?.querySelector?.(".dx-datagrid-rowsview");
  const canReorder = props.enablePositionsReorder;
  const canAttachOnly = props.allowDragPositionToTree && !props.enablePositionsReorder;

  if (!canReorder && !canAttachOnly) {
    detachRowsViewPositionDnDListeners();
    return;
  }
  if (!rowsView) return;

  detachRowsViewPositionDnDListeners();
  const cleanups = [];

  if (canReorder) {
    const onDragOver = (ev) => {
      if (!isPositionReorderDragActive()) return;
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = "move";
      updatePositionReorderDropIndicator(ev, rowsView);
    };
    const onDragLeave = (ev) => {
      if (rowsView.contains(ev.relatedTarget)) return;
      clearPositionReorderDropIndicator();
    };
    const onDrop = (ev) => handlePositionsGridRowDrop(ev);
    rowsView.addEventListener("dragover", onDragOver);
    rowsView.addEventListener("dragleave", onDragLeave);
    rowsView.addEventListener("drop", onDrop);
    cleanups.push(() => {
      rowsView.removeEventListener("dragover", onDragOver);
      rowsView.removeEventListener("dragleave", onDragLeave);
      rowsView.removeEventListener("drop", onDrop);
    });
  }

  if (canAttachOnly) {
    const paneRight = positionsGridWrapperRef.value?.closest?.(".pane-view-right");
    const onAttachDragOver = (ev) => {
      if (!positionTreeDragSessionActive) return;
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = "none";
    };
    const onAttachDrop = (ev) => {
      if (!positionTreeDragSessionActive) return;
      ev.preventDefault();
      finishPositionTreeDrag();
    };
    const attachTargets = [rowsView, paneRight].filter(Boolean);
    attachTargets.forEach((target) => {
      target.addEventListener("dragover", onAttachDragOver);
      target.addEventListener("drop", onAttachDrop);
      cleanups.push(() => {
        target.removeEventListener("dragover", onAttachDragOver);
        target.removeEventListener("drop", onAttachDrop);
      });
    });
  }

  rowsViewPositionDnDCleanup = () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}

function applyPositionsGridWidthStyle(gridEl) {
  if (!gridEl?.style) return;
  Object.assign(gridEl.style, POSITIONS_GRID_ROOT_STYLE);
}

function onPositionsGridContentReady(e) {
  scheduleRowsViewPositionDnDAttach();
  applyPositionsGridWidthStyle(e?.element);
}

function onPositionTreeDragStart(event, position) {
  const canAttachTree = props.allowDragPositionToTree;
  const canHtml5Reorder = props.enablePositionsReorder;
  if (!canAttachTree && !canHtml5Reorder) return;
  if (!event.dataTransfer || !position) return;
  event.stopPropagation();
  const positionId = position.id;
  if (positionId == null) return;
  const positionName = String(position.name || "").trim();
  const codeStr = position.code ? `#${position.code}` : "";
  const positionCodeStr = codeStr
    ? `${codeStr}${positionName ? ` - ${positionName}` : ""}`.trim()
    : positionName;
  const payload = {
    positionId,
    sourceDepartmentId: props.sourceDepartmentId ?? null,
    positionName,
    positionCodeStr,
  };
  if (props.enablePositionsReorder) {
    const si = gridPositions.value.findIndex((p) => String(p.id) === String(positionId));
    payload.sourceRowIndex = si >= 0 ? si : 0;
  }
  event.dataTransfer.setData("application/x-exiqtive-position-department", JSON.stringify(payload));
  event.dataTransfer.setData("text/plain", "position-to-department");
  event.dataTransfer.effectAllowed = canAttachTree ? "copyMove" : "move";
  setEmptyNativeDragImage(event);

  const rowEl =
    event.target.closest("tr.dx-data-row") ||
    event.target.closest("tr.dx-row") ||
    event.target.closest("tr");

  const deferAttachUi =
    useUnifiedPositionDragHandle.value || (canAttachTree && !props.enablePositionsReorder);

  if (props.onPositionDragStartCallback) {
    props.onPositionDragStartCallback({
      positionId,
      sourceDepartmentId: props.sourceDepartmentId ?? null,
      positionName,
      positionCodeStr,
      clientX: event.clientX,
      clientY: event.clientY,
      deferAttachUi,
    });
  }

  if (!rowEl) return;

  if (positionTreeDragSessionActive) {
    finishPositionTreeDrag();
  }
  clearPositionDragGhost();
  positionTreeDragSessionActive = true;
  attachPositionTreeDragEndListener();
  positionDragGhostRowEl = rowEl;
  rowEl.classList.add("position-role-reorder-drag-source");

  if (props.enablePositionsReorder) {
    positionReorderDropRowsCache = null;
    const si = gridPositions.value.findIndex((p) => String(p.id) === String(positionId));
    positionReorderSourceRowIndex = si >= 0 ? si : 0;
    positionDragUiMode = "reorder";
    createPositionDragGhost(event, rowEl, "reorder");
    attachPositionDragUnifiedDragoverListener();
  } else if (canAttachTree) {
    positionDragUiMode = "attach";
    createPositionDragGhost(event, rowEl, "attach");
    attachPositionDragUnifiedDragoverListener();
  }
}

function onPositionTreeDragEnd() {
  finishPositionTreeDrag();
}

onBeforeUnmount(() => {
  finishPositionTreeDrag();
  detachRowsViewPositionDnDListeners();
});

defineExpose({
  finishPositionDragSession: finishPositionTreeDrag,
});

function resolvePositionMenuItems(row) {
  if (typeof props.getPositionMenuItems === "function") {
    return props.getPositionMenuItems(row) || [];
  }
  return props.positionMenuItems;
}

function getOccupiedByProps(occupiedBy) {
  const name = occupiedBy?.name?.trim() || "";
  const parts = name.split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}
</script>

<style scoped>
.department-card {
  width: 100%;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow:
    0px 1px 3px rgba(16, 24, 40, 0.1),
    0px 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.department-card__header-wrapper {
  background: #ffffff;
}

.department-card__breadcrumbs {
  /* Align the trail with the header's own 10px/16px padding; the shared
   * ExBreadcrumbs root forces padding:0 !important, so use margin here. */
  margin: 10px 16px 0;
}

/* ER-10044 / ER-9857 — readiness scope toggle, matched to FilterPositionsModal's
 * scope toggle (text #344054 on #fff = 10.46:1; active #6941c6 on #f9f5ff = 6.16:1). */
.department-card__readiness-scope {
  display: flex;
  width: fit-content;
  margin: 12px 16px 0 auto;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
}

.department-card__readiness-scope-option {
  appearance: none;
  border: 0;
  background: #ffffff;
  padding: 8px 14px;
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}

.department-card__readiness-scope-option--active {
  background: #f9f5ff;
  color: #6941c6;
}

.department-card__readiness-scope-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.department-card__readiness-scope-divider {
  width: 1px;
  align-self: stretch;
  background: #d0d5dd;
}

.department-card__tabs :deep(.container) {
  padding: 0 24px;
  max-width: 100%;
}

.department-card__tabs :deep(.menu-row) {
  margin-top: 0;
  margin-bottom: 0;
}

.department-card__body {
  min-width: 0;
  background: #ffffff;
}

.department-card__filters {
  padding: 20px;
  min-height: 84px;
}

/*
 * idea-MDP-450: Vuetify clamps .v-label to max-width 90% with ellipsis.
 * Widen the search field and allow the label to use the full input width.
 */
.department-card--idea-mdp-450 .department-card__filters :deep(.ex_text_input.filter-input-search) {
  width: 350px !important;
  max-width: 100%;
}

.department-card--idea-mdp-450 .department-card__filters :deep(.v-text-field .v-label) {
  max-width: 100% !important;
}

/* Unassigned panel: reclaim empty filter column so the field can grow in the split pane */
.department-card__filters--wide-search :deep(.filter-button) {
  display: none;
}

.department-card__filters--wide-search :deep(.search-button) {
  flex: 1 1 auto;
  max-width: 100%;
  width: 100%;
  justify-content: flex-end;
  padding-left: 0;
  padding-right: 0;
}

.department-card__filters--wide-search :deep(.ex_text_input.filter-input-search) {
  width: 100% !important;
  min-width: 0;
  max-width: 100%;
  float: none;
  margin-top: 0;
}

.department-card__btn-primary {
  border-radius: 8px;
  border: 1px solid #7f56d9;
  background: #7f56d9;

  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  display: flex;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: var(--White, #fff);
  padding-inline: 14px;
  padding-block: 8px;

  /* Text sm/Semibold */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
}

.department-card__btn-primary:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.department-card__chatter-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  cursor: pointer;
  border-radius: 8px;
}

.department-card__chatter-cell:hover {
  background-color: #f2edff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.06);
}

.department-card__menu-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* DevExtreme grid overrides — scoped to this card via :deep() so they never leak
   into other DxDataGrid instances on the same page. */
.department-card :deep(.department-card-grid.dx-datagrid) {
  background: #fff;
  height: auto !important;
  max-height: none !important;
}

.department-card :deep(.department-card-grid .dx-datagrid-headers) {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  background-color: #f9fafb !important;
  border-top: 1px solid #eaecf0;
}

.department-card :deep(.department-card-grid .dx-datagrid-headers .dx-datagrid-table .dx-row > td) {
  padding: 13px 24px !important;
}

.department-card
  :deep(.department-card-grid .dx-datagrid-headers .dx-datagrid-table .dx-row > td span) {
  white-space: nowrap;
}

.department-card-positions-grid-wrapper.roles-table-wrapper
  :deep(.department-card-grid .dx-datagrid-content .dx-datagrid-table .dx-row > td) {
  vertical-align: middle;
  padding: 12px 20px;
  overflow: visible;
}

/* Hide default cell text in data rows only (custom templates render content). */
.department-card
  :deep(
    .department-card-grid
      .dx-datagrid-rowsview
      .dx-row
      > td:not(.dx-command-drag)
      .dx-datagrid-text-content
  ) {
  display: none;
}

.department-card :deep(.department-card-grid .dx-header-row > td > .dx-datagrid-text-content) {
  display: inline-table;
}

.department-card :deep(.department-card-grid .dx-datagrid-headers .dx-datagrid-table .dx-row > td),
.department-card :deep(.department-card-grid .dx-datagrid .dx-row-lines > td) {
  border-bottom: 1px solid #eaecf0 !important;
}

.department-card :deep(.department-card-grid .dx-datagrid-rowsview) {
  border-top: none;
  overflow: visible !important;
}

.department-card :deep(.department-card-grid .dx-scrollable-wrapper),
.department-card :deep(.department-card-grid .dx-scrollable-container) {
  overflow-x: visible !important;
  overflow-y: hidden !important;
  max-height: none !important;
}

.department-card :deep(.department-card-grid .dx-scrollbar-vertical),
.department-card :deep(.department-card-grid .dx-scrollable-scrollbar-vertical) {
  display: none !important;
}

.department-card :deep(.department-card-grid .department-card__drag-handle) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: grab;
}

.department-card :deep(.department-card-grid .department-card__drag-handle:active) {
  cursor: grabbing;
}

.department-card :deep(.department-card-grid .department-card__position-object) {
  min-width: 0;
  max-width: 100%;
}

.department-card :deep(.department-card-grid .department-card__employee-cell) {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  overflow: hidden;
  padding: 4px 0;
}

.department-card :deep(.department-card-grid .department-card__employee-cell .employee-info) {
  min-width: 0;
  overflow: hidden;
}

.department-card :deep(.department-card-grid .department-card__employee-cell .employee-name),
.department-card :deep(.department-card-grid .department-card__employee-cell .text_name),
.department-card :deep(.department-card-grid .department-card__employee-cell .text_position) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.department-card-positions-grid-wrapper .drag-handle-cell-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;
}

.department-card-positions-grid-wrapper .drag-handle-cell-wrapper:active {
  cursor: grabbing;
}

.department-card-positions-grid-wrapper.roles-table-wrapper--with-drag-col
  .drag-handle-cell-wrapper.position-role-tree-drag-handle {
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
}

.department-card-positions-grid-wrapper.roles-table-wrapper--with-drag-col
  .department-card__position-object {
  left: -6px;
}

.department-card-positions-grid-wrapper {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.department-card-positions-grid-wrapper
  :deep(.department-card-grid .dx-datagrid-headers .dx-datagrid-content),
.department-card-positions-grid-wrapper
  :deep(.department-card-grid .dx-datagrid-rowsview .dx-datagrid-content) {
  overflow: visible !important;
}

.department-card-positions-grid-wrapper :deep(.department-card-grid .dx-datagrid-table) {
  width: max-content !important;
  min-width: 100% !important;
  table-layout: auto !important;
}

.department-card-positions-grid-wrapper .position-role-drag-grid-icon {
  flex-shrink: 0;
  display: block;
}

.department-card-positions-grid-wrapper :deep(td.position-attached-role-unified-col),
.department-card-positions-grid-wrapper
  :deep(.dx-datagrid-headers .position-attached-role-unified-col) {
  min-width: 52px !important;
  width: 52px !important;
  max-width: 52px;
  box-sizing: border-box;
  overflow: visible;
}

.department-card-positions-grid-wrapper :deep(td.position-attached-role-unified-col) {
  padding: 12px 6px 12px 16px !important;
}

.department-card-positions-grid-wrapper
  :deep(.dx-datagrid-headers .position-attached-role-unified-col) {
  padding: 13px 6px 13px 16px !important;
}

.department-card-positions-grid-wrapper tr.position-role-reorder-drag-source {
  opacity: 0.45;
}

.department-card-positions-grid-wrapper .role-position-drag-ghost--reorder .dx-datagrid-table {
  background: #fff;
}
</style>

<style>
/* Drag ghost is portaled to document.body — keep row layout aligned with the live grid. */
.role-position-drag-ghost.department-card-position-drag.roles-table-wrapper .dx-datagrid-table td {
  overflow: visible !important;
  vertical-align: middle;
}

.role-position-drag-ghost.department-card-position-drag.roles-table-wrapper
  .org-object-card-compass {
  max-width: none !important;
}

.role-position-drag-ghost.department-card-position-drag.roles-table-wrapper
  .org-object-card-compass
  .code-text,
.role-position-drag-ghost.department-card-position-drag.roles-table-wrapper
  .org-object-card-compass
  .name-text {
  max-width: none !important;
}

.role-position-drag-ghost--reorder.department-card-position-drag.roles-table-wrapper
  .dx-datagrid-table
  td {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.role-position-drag-ghost--reorder.department-card-position-drag.roles-table-wrapper
  .dx-datagrid-table
  td:first-of-type {
  padding-left: 14px !important;
}
</style>

<style scoped>
.position-role-reorder-drop-indicator {
  height: 2px;
  background-color: #337ab7;
  pointer-events: none;
  z-index: 10000;
}
</style>
